import { FC, useState } from 'react';
import { Formik } from 'formik';
import { Form } from 'react-router-dom';
import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';

import ThunderIcon from '../../../../../../../assets/icons/thunder-lightning-fast.png';
import BalanceIcon from '../../../../../../../assets/icons/stash_balance-light.png';
import SofaIcon from '../../../../../../../assets/icons/solar_sofa-linear.png';
import InformationIcon from '../../../../../../../assets/icons/Info, Infornation.png';

import RadioOption from '../../../../../../../common/RadioOption';
import ToggleRadioButton from '../../../../../../../common/ToggleRadioButton';
import CustomButton from '../../../../../../../common/CustomButton';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks/redux';
import { useUpdateUserAdditionalInfoMutation } from '../../../../../../../store/services/customizeOptionsApi';
import { setUserInfo } from '../../../../../../../store/slices/userSlice';

import styles from './PreferredPace.module.scss';

type FormikValues = {
  pace?: string;
  planningStyle?: string;
}

type PreferredPaceProps = {
  closeModal: () => void;
}

const PreferredPace: FC<PreferredPaceProps> = ({ closeModal }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user);
  const [updateUserAdditionalInfo, { isSuccess }] = useUpdateUserAdditionalInfoMutation();

  const [resErrors, setResErrors] = useState<{attr: string | null, code: string, detail: string}[] | null>(null);

  const initialValues = {
    pace: user.pace_preference,
    planningStyle: user.trip_planning_style,
  };

  const handleSubmit = async (values: FormikValues) => {
    const payload = {
      pace_preference: values.pace,
      trip_planning_style: values.planningStyle,
    };

    try {
      await updateUserAdditionalInfo(payload);
      dispatch(setUserInfo(payload));
      setResErrors(null);
      closeModal();
    } catch (error) {
      console.error("Error updating user info:", error);
      setResErrors((error as any)?.data?.errors || null);
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: FormikValues = {};

    return errors;
  }

  return (
    <Box className={styles.preferredPace}>
      <Formik
        initialValues={initialValues}
        validate={formikValidate}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleSubmit, isSubmitting }) => (
          <Form className={styles.preferredPaceForm} onSubmit={handleSubmit}>
            <FormControl component="fieldset" className={styles.radioGroupWrapper}>
              <Typography variant='h5Bold'className={styles.stepTitle}>Let's personalize your AI assistant</Typography>
              <Typography variant='h10' className={styles.stepSubtitle}>Preferred Pace</Typography>
              <RadioGroup name="pace" className={styles.radioGroup} value={values.pace} onChange={handleChange}>
                {[
                  { value: "Fast-paced", name: "Fast-Paced", icon: ThunderIcon },
                  { value: "Balanced", name: "Balanced", icon: BalanceIcon },
                  { value: "Slow & Relaxed", name: "Slow & Relaxed", icon: SofaIcon }
                ].map((val) => (
                  <FormControlLabel
                    key={val.value}
                    value={val.value}
                    control={<Radio sx={{ display: "none" }} />}
                    label={<RadioOption value={val.value} icon={val.icon} label={val.name.replace("_", " ")}
                      selected={values.pace === val.value}
                    />}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" className={`${styles.radioGroupWrapper} ${styles.mt35}`}>
              <FormLabel component="legend">Trip Planning Style</FormLabel>
              <RadioGroup name="planningStyle" className={styles.radioGroup} value={values.planningStyle} onChange={handleChange}>
                {["Structured", "Flexible", "Spontaneous"].map((val) => (
                  <FormControlLabel
                    key={val}
                    value={val}
                    control={<Radio sx={{ display: "none" }} />}
                    label={<ToggleRadioButton value={val} label={val} selected={values.planningStyle === val} />}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <Box className={styles.additionalInfo}>
              <Typography variant='h11' className={styles.additionalInfoText}>To get badges, fill in all the questions</Typography>
              <img src={InformationIcon} className={styles.additionalInfoImg} alt='info' />
            </Box>

            <Box className={styles.cityAndAiroportBtns}>
              <CustomButton
                btnType='button'
                variant='outlined'
                onClick={closeModal}
              >
                Cancel
              </CustomButton>
              <CustomButton
                btnType='submit'
                customClass={styles.customButton}
                disabled={isSubmitting}
              >
                Save
              </CustomButton>
            </Box>
            {isSuccess && <Typography variant='h12' sx={{ color: '#208B64' }}>
              Preferred Pace updated successfully.
            </Typography>}
            {resErrors && resErrors.map((error, index) => (
              <Typography key={index} variant='h12' sx={{ color: '#C8102E' }}>
                {error.detail}
              </Typography>
            ))}
          </Form>)}
      </Formik>
    </Box>
  )
}

export default PreferredPace;
